import { type FC, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGetWorkflow, useListWorkflowExecutions, useStartWorkflow } from "@/queries/automation-queries";
import {
    workflowName,
    workflowExecutionsUrl,
    workflowCreatedDate,
    workflowCreatorName,
    workflowsUrl,
    workflowDetailsEditUrl,
} from "@/utils/automations";
import { WorkflowExecutionsList } from "@/components/automations/AutomationExecutionsList";
import { WorkflowActionsDropdown } from "@/components/automations/AutomationActionsDropdown";
import { ErrorMessage } from "@/components/ErrorMessage";
import { Text } from "@/components/podkit/typography/Text";
import { WorkflowTriggerSection } from "@/components/automations/AutomationTriggerSection";
import { WorkflowActionSection } from "@/components/automations/AutomationActionSection";
import { Button } from "@/components/flexkit/Button";
import { Card } from "@/components/flexkit/Card";
import { IconPlay } from "@/assets/icons/geist/IconPlay";
import { withGitpodSize } from "@/assets/icons/utils";
import IconPencil from "central-icons/IconPencil";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/podkit/tabs/Tabs";
import { useMembers } from "@/queries/organization-queries";
import { formatShortDate } from "@/format/time";
import Breadcrumbs from "@/components/Breadcrumb";
import { BreadcrumbsSkeleton } from "@/components/BreadcrumbsSkeleton";
import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";

const IconPencilWithSize = withGitpodSize(IconPencil);

enum WorkflowDetailsTab {
    Definition = "definition",
    Executions = "executions",
}

export const AutomationDetailsPage: FC = () => {
    const { workflowId } = useParams<{ workflowId: string }>();
    const navigate = useNavigate();
    const { toast } = useToast();
    const { data: workflow, error: workflowError } = useGetWorkflow(workflowId);
    const { data: workflowExecutions, error: workflowExecutionsError } = useListWorkflowExecutions({
        enabled: !!workflowId,
        workflowId: workflowId || "",
    });
    const startWorkflow = useStartWorkflow();
    const { data: members } = useMembers();
    const [selectedTab, setSelectedTab] = useState<WorkflowDetailsTab>(WorkflowDetailsTab.Executions);

    const handleRunWorkflow = async () => {
        if (!workflow?.id) return;

        try {
            const execution = await startWorkflow.mutateAsync(workflow.id);
            navigate(workflowExecutionsUrl({ id: execution.id }));
        } catch (error) {
            toast({
                title: "Failed to start automation",
                description: formatError(error),
            });
        }
    };

    const handleEdit = useCallback(() => {
        if (!workflow?.id) return;
        navigate(workflowDetailsEditUrl({ id: workflow.id }));
    }, [navigate, workflow?.id]);

    const createdDate = workflowCreatedDate(workflow);
    const creatorName = workflowCreatorName(workflow, members?.members);
    
    const creator = workflow?.metadata?.creator?.id && members?.members
        ? members.members.find(m => m.userId === workflow.metadata?.creator?.id)
        : undefined;

    return (
        <div data-testid="workflow-page" className="flex size-full flex-col gap-4">
            {workflow ? (
                <div>
                    <div className="flex items-start justify-between">
                        <div className="flex flex-col gap-3">
                            <Breadcrumbs
                                customBreadcrumbs={[
                                    { label: "Automations", to: workflowsUrl() },
                                    { label: workflowName(workflow) },
                                ]}
                            />
                            <div className="flex flex-col gap-2">
                                <Text className="text-md text-content-strong">{workflow.metadata?.description}</Text>
                                {createdDate && creatorName && (
                                    <div className="flex items-center gap-1.5 text-base text-content-muted">
                                        <span>Created on {formatShortDate(createdDate, true)} by</span>
                                        {creator && (
                                            <Avatar size={16} className="size-5 border-[0.65px] border-button-clear-accent">
                                                {creator.avatarUrl && (
                                                    <AvatarImage src={creator.avatarUrl} alt={`${creatorName}'s avatar`} />
                                                )}
                                                <AvatarFallback>
                                                    <AvatarInitials name={creatorName} size={16} className="text-[10px] leading-5" />
                                                </AvatarFallback>
                                            </Avatar>
                                        )}
                                        <span>{creatorName}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <WorkflowActionsDropdown workflow={workflow} />
                            <Button
                                variant="primary"
                                LeadingIcon={IconPlay}
                                onClick={handleRunWorkflow}
                                disabled={startWorkflow.isPending}
                                loading={startWorkflow.isPending}
                                data-tracking-id="run-workflow-workflow-details-page"
                            >
                                Run
                            </Button>
                        </div>
                    </div>
                </div>
            ) : (
                <BreadcrumbsSkeleton itemsCount={2} />
            )}

            <ErrorMessage error={workflowError} />

            <Tabs
                value={selectedTab}
                onValueChange={(value) => setSelectedTab(toWorkflowDetailsTab(value))}
                className="flex flex-col gap-3"
            >
                <div className="flex items-center justify-between">
                    <TabsList className="w-fit">
                        <TabsTrigger value={WorkflowDetailsTab.Executions}>Executions</TabsTrigger>
                        <TabsTrigger value={WorkflowDetailsTab.Definition}>Definition</TabsTrigger>
                    </TabsList>
                    <Button
                        variant="outline"
                        size="sm"
                        LeadingIcon={IconPencilWithSize}
                        onClick={handleEdit}
                        data-tracking-id="edit-workflow-tabs-button"
                    >
                        Edit
                    </Button>
                </div>

                <TabsContent value={WorkflowDetailsTab.Definition} className="flex flex-col gap-4">
                    <Card variant="bordered" className="flex flex-col gap-4 bg-transparent">
                        <WorkflowTriggerSection triggers={workflow?.spec?.triggers} />
                        <WorkflowActionSection action={workflow?.spec?.action} />
                    </Card>
                </TabsContent>

                <TabsContent value={WorkflowDetailsTab.Executions} className="flex flex-col gap-4">
                    <ErrorMessage error={workflowExecutionsError} />
                    <WorkflowExecutionsList
                        executions={workflowExecutions || []}
                        onRunWorkflow={handleRunWorkflow}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
};

function toWorkflowDetailsTab(value: string): WorkflowDetailsTab {
    if (Object.values(WorkflowDetailsTab).includes(value as WorkflowDetailsTab)) {
        return value as WorkflowDetailsTab;
    }
    throw new Error(`Invalid WorkflowDetailsTab value: ${value}`);
}
